import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Avatar from "../components/Avatar";
import VerifiedBadge from "../components/VerifiedBadge";
import { Heart, MessageCircle, UserPlus, Bell, Target, MailQuestion } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}

const TYPE_META = {
  follow: { icon: UserPlus, label: "started following you" },
  like: { icon: Heart, label: "liked your highlight" },
  comment: { icon: MessageCircle, label: "commented on your match" },
  match: { icon: Target, label: "matches your saved search" },
  message_request: { icon: MailQuestion, label: "wants to message you — review it in Messages" },
};

export default function NotificationsPage() {
  const [notifs, setNotifs] = useState(null);

  useEffect(() => {
    api.get("/notifications").then(({ data }) => {
      setNotifs(data);
      api.post("/notifications/read").catch(() => {});
    });
  }, []);

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="notifications-page">
      <h1 className="font-display text-4xl uppercase tracking-tight text-white mb-6">
        Notifications
      </h1>

      {notifs === null ? (
        <div className="text-[#737373]">Loading…</div>
      ) : notifs.length === 0 ? (
        <div
          data-testid="notifications-empty"
          className="bg-[#121212] border border-[#262626] p-10 rounded-sm text-center"
        >
          <Bell className="w-8 h-8 text-[#404040] mx-auto mb-3" />
          <div className="text-[#a3a3a3] text-sm">
            No activity yet. Post a highlight and get noticed.
          </div>
        </div>
      ) : (
        <ul className="space-y-2">
          {notifs.map((n) => {
            const meta = TYPE_META[n.type] || TYPE_META.like;
            const Icon = meta.icon;
            return (
              <li
                key={n.id}
                data-testid={`notification-${n.id}`}
                className={`flex items-start gap-3 border p-4 rounded-sm transition-colors ${
                  n.read
                    ? "bg-[#121212] border-[#262626]"
                    : "bg-[#1a1a1a] border-[#404040]"
                }`}
              >
                <Link to={`/profile/${n.actor?.id}`} className="shrink-0">
                  <Avatar url={n.actor?.avatar_url} name={n.actor?.name} size={42} />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white">
                    <Link
                      to={`/profile/${n.actor?.id}`}
                      className="font-semibold hover:underline inline-flex items-center gap-1"
                    >
                      {n.actor?.name}
                      <VerifiedBadge role={n.actor?.role} verified={n.actor?.verified} size={12} />
                    </Link>{" "}
                    <span className="text-[#a3a3a3]">{meta.label}</span>
                  </div>
                  {n.text && (
                    <div className="text-sm text-[#e5e5e5] mt-1 bg-[#0a0a0a] border border-[#262626] px-3 py-2 rounded-sm truncate">
                      “{n.text}”
                    </div>
                  )}
                  <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
                    {timeAgo(n.created_at)}
                  </div>
                </div>
                <div className="shrink-0 p-2 border border-[#262626] rounded-sm">
                  <Icon
                    className={`w-4 h-4 ${
                      n.type === "like" ? "text-red-500" : "text-[#a3a3a3]"
                    }`}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
